import {
  ApiGeneralDataResponse,
  CoinName,
  ConfigurationItem,
  FormattedChartDataPoint,
  IdToValue,
  LongShort,
  MainCoinAmountData,
  MarginTypes,
  PrecisionConfig,
  StopType,
  TimeRange,
  TradeGraphData,
  TradeLadderLevel,
  TradeNextOrders,
  UnixTimestamp,
  UpdatedRawChartData,
} from "@shared/types";
import {
  ChannelValue,
  Empty,
  EntryGraceBaseline,
  LeverageType,
  MultiToggleValue,
  MultiValue,
  Off,
  StrategyBaseline,
  PercentageBaseType,
} from "@shared/features/bots/BotForm/common/types";

export enum AmountType {
  PERCENTAGE = 1,
  FIXED_BTC_AMOUNT = 2,
  RISK_PERCENTAGE = 3,
  FIXED_USD_AMOUNT = 4,
}

export enum BotType {
  TRADING_VIEW = 1,
  ASAP = 2,
  SIGNAL = 3,
  GRID = 4,
  TRADING_VIEW_SIGNALS = 5,
  HEDGING = 6,
}

export enum QueryBotType {
  TRADING_VIEW = "tradingview",
  TRADING_VIEW_SIGNALS = "tradingview-signals",
  ASAP = "dca",
  SIGNAL = "signals",
  GRID = "grid",
  HEDGING = "hedging",
}

export enum StrategyStyleType {
  CUSTOM = 1,
  DCA = 2,
  BUILT_IN = 3,
  LEGACY_BUILT_IN = 4,
}

export enum OrderBaseline {
  FIRST_ENTRY = 1,
  FIRST_TAKE_PROFIT = 2,
  AVERAGE_ENTRIES = 3,
  LAST_ENTRY = 4,
  ORIGINAL_FIRST_ENTRY = 5,
}

export interface BaseStrategyData {
  price_difference?: number | MultiValue | ChannelValue;
  price_scale?: number | MultiValue | ChannelValue;
  number_of_active_orders?: number | null | MultiValue | ChannelValue;
  baseline?: StrategyBaseline | MultiValue | ChannelValue;
  amount_scale?: number | MultiValue | ChannelValue;
  number_of_orders?: number | MultiValue | ChannelValue;
}

export interface RawBaseOrderData {
  price_difference?: number;
  price_scale?: number;
  number_of_active_orders?: number | null;
}

export type ExchangeToSymbolPrices = Record<string, Record<string, string>>;

export interface DCAStrategyData extends BaseStrategyData {
  strategy_style: StrategyStyleType.DCA | MultiValue | ChannelValue;
  first_order_percentage?:
    | number
    | MultiValue
    | MultiToggleValue
    | ChannelValue;
}

export interface ParsedBaseStrategyData {
  price_difference?: number | Empty;
  price_scale?: number | Empty;
  number_of_active_orders?: number | null | Empty;
}

export interface ParsedDCAStrategyData extends ParsedBaseStrategyData {
  strategy_style: StrategyStyleType.DCA | Empty;
  number_of_orders?: number | Empty;
  amount_scale?: number | Empty;
  first_order_percentage?: number | Empty;
}

export interface ParsedCustomStrategyData extends ParsedBaseStrategyData {
  strategy_style: StrategyStyleType.CUSTOM | Empty;
  targets: number[] | Empty;
}

export interface CustomStrategyData extends BaseStrategyData {
  strategy_style: StrategyStyleType.CUSTOM | MultiValue | ChannelValue;
  targets?: (number | string)[] | MultiValue | ChannelValue;
}

export interface BuiltInStrategyData extends BaseStrategyData {
  strategy_style: StrategyStyleType.BUILT_IN | MultiValue | ChannelValue;
  builtin_strategy_type?: StrategyType | ChannelValue;
  name?: string;
}

export interface RawCustomStrategyData extends RawBaseOrderData {
  strategy_style: StrategyStyleType.CUSTOM;
  targets?: number[];
}

export interface RawBuiltInStrategyData extends RawBaseOrderData {
  strategy_style: StrategyStyleType.BUILT_IN;
  type: StrategyType;
  name: string;
}

export interface RawDCAStrategyData extends RawBaseOrderData {
  strategy_style: StrategyStyleType.DCA;
  number_of_orders?: number;
  amount_scale?: number;
  first_order_percentage?: number;
  baseline?: StrategyBaseline;
}

export interface DefaultOrder<T = number> {
  percent?: T | MultiValue | ChannelValue;
  baseline?: OrderBaseline | MultiValue;
}

export interface AmountConfiguration {
  type: AmountType | MultiValue;
  value: number | MultiValue;
  percentage_base_type: PercentageBaseType;
  custom_base_value?: number;
}

export type StrategyData =
  | DCAStrategyData
  | CustomStrategyData
  | BuiltInStrategyData;
export type ParsedStrategyData =
  | ParsedDCAStrategyData
  | ParsedCustomStrategyData;
export type RawStrategyData =
  | RawDCAStrategyData
  | RawCustomStrategyData
  | RawBuiltInStrategyData;
export type UnifiedStrategyData = DCAStrategyData & {
  targets?: number[];
  strategy_style: StrategyStyleType;
} & { builtin_strategy_type?: StrategyType };

export interface FormTrailingStopData<
  T extends number | MultiValue = number | MultiValue
> {
  type: T;
  value: T | undefined;
  trigger: {
    type: T | undefined;
    value: T | undefined;
  };
}

export interface AdvancedSettings<T = number | null> {
  number_of_trades_till_bot_stop: T;
  cooldown_between_opening_trades: T;
  cooldown_after_trade_close: T;
  number_of_simultaneous_trades: T;
  cooldown_between_opening_trades_per_symbol: T;
  min_symbol_price: T;
  min_symbol_volume: T;
  concurrent_max_usd_invested: T;
  number_of_simultaneous_trades_per_symbol: T;
  auto_cancel_timeout_seconds: T;
}

export enum BotGraphType {
  PROFIT_BY_DAY = 1,
  ACCUMULATING_PROFIT = 2,
  PROFIT_BY_DAY_PERCENTAGE = 3,
  ACCUMULATING_PROFIT_PERCENTAGE = 4,
}

export interface BotMetadata {
  active_trade_id?: number;
  long_active_trade_id?: number;
  short_active_trade_id?: number;
  bot_type: BotType;
  exchange: string;
  symbols: string[] | undefined;
  name: string;
  client_id: number;
}

export interface BaseBotRequest {
  dca_bot_id: number;
  is_channel_configuration?: boolean;
  is_backtesting?: boolean;
  is_demo_query?: boolean;
}

export interface BasePublicBotRequest {
  dca_bot_uuid: string;
  is_demo_query?: boolean;
}

export interface BulkChangeBotsStatusRequest {
  bot_ids: number[];
  is_active: boolean;
  should_cancel_trade?: boolean;
  ratio?: number;
  bot_type: BotType;
}

export interface ChangeBotPublicityRequest extends BaseBotRequest {
  is_public: boolean;
}

export type BlacklistedOperationHours = {
  day: number;
  start_hour: string;
  end_hour: string;
  timezone: string;
};

export interface CreateBotRequest
  extends AdvancedSettings<number | null | Empty>,
    OverrideData {
  bot_type: BotType;
  dca_bot_name: string | Empty;
  account_ids?: number[] | Empty;
  exchanges?: string[] | Empty;
  symbols: string[];
  blacklist_symbols: string[];
  long_short: LongShort | Empty | undefined;
  margin_type: MarginTypes | Empty | null;
  multiplier: number | Empty | undefined;
  stop_timeout: number | Empty | undefined;
  trailing_entry: number | Empty | null;
  trailing_take_profit?: number | Empty | null;
  trailing_take_profit_last_target_only?: boolean | Empty | null;
  trailing_stop: FormTrailingStopData | null;
  default_stop: DefaultOrder | null;
  default_take_profit: DefaultOrder | null;
  amount_configuration: AmountConfiguration;
  entry_strategy?: ParsedStrategyData | null;
  take_profit_strategy: ParsedStrategyData | null;
  number_of_continuous_stops_till_bot_stop: number | Empty | null;
  symbol_to_leverage_data?: Record<
    string,
    [
      MarginTypes | Empty | undefined,
      number | Empty | undefined,
      LeverageType | undefined
    ]
  >;
  status?: BotStatus.ACTIVE | BotStatus.INACTIVE | Empty;
  copied_from?: number;
  sub_group_ids?: number[];
  is_channel_configuration?: boolean;
  channel_configuration_id?: number | null;
  max_grid_price?: number | Empty | null;
  cancel_on_profit_before_entry?: boolean | Empty | null;
  entry_grace_percent?: number | Empty | null;
  entry_grace_baseline?: EntryGraceBaseline;
  entry_grace_target_exchange?: string | null;
  stop_type?: StopType | null;
  stop_limit_price_reduction?: number | null | Empty;
  skip_without_stoploss: boolean;
  alternative_usd_pairs?: string[];
  follow_futures_as_spot?: boolean | null;
  take_profit_grace_percent?: number | Empty | null;
  take_profit_grace_target_exchange?: string | null;
  blacklisted_operation_hours?: BlacklistedOperationHours[];
  keyword_to_symbol_mapping?: Record<string, string>;
  long_exposure_ratio?: number | Empty | null;
  lower_price_long_exposure_ratio?: number | Empty | null;
  upper_price_long_exposure_ratio?: number | Empty | null;
}

export type EditBotRequest = CreateBotRequest &
  BaseBotRequest & { should_display_edited_bot?: boolean };
export type EditBotsRequest = CreateBotRequest & {
  bot_ids: number[];
};
export type EditBotConfigurationRequest = CreateBotRequest & {
  is_channel_configuration: boolean;
  exchange: string;
};

export interface GetBotsGraphsDataRequest {
  bot_type: BotType;
  time_range: TimeRange;
  time_aggregation?: TimeRange;
  include_archived: boolean;
}

export interface BaseGetBotGraphDataRequest {
  graph_type: BotGraphType;
  time_range: TimeRange;
  time_aggregation: TimeRange;
}

export type GetBotGraphDataRequest = BaseBotRequest &
  BaseGetBotGraphDataRequest;
export type GetPublicBotGraphDataRequest = BasePublicBotRequest &
  BaseGetBotGraphDataRequest;

export interface BotOptions {
  "Amount Type": Options;
  "Amount Value": Options;
  "Amount Custom Base Value": Options;
  "Amount Percentage Base Type": Options;
  "Default Stop Baseline": Options;
  "Default Stop Percentage": Options;
  "Default Take-Profit Baseline": Options;
  "Default Take-Profit Percentage": Options;
  "Long/Short"?: Options;
  "Margin Type"?: [{ [symbol: string]: Options }];
  Multiplier?: [{ [symbol: string]: Options }];
  "Stop Timeout": Options;
  "Entry Grace Percentage": Options;
  "Stop Type": Options;
  "Take Profit Grace Percentage": Options;
}

export interface BotConfigurations {
  "Entry Strategy": ConfigurationItem;
  "Take-Profit Strategy": ConfigurationItem;
  "Trailing Take-Profit": ConfigurationItem;
  "Trailing Entry": ConfigurationItem;
  "Trailing Stop": ConfigurationItem;
}

export interface BaseBotGeneralData {
  options: BotOptions;
  configurations: BotConfigurations;
}

export interface BotGeneralData extends BaseBotGeneralData {
  options: BotOptions;
  configurations: BotConfigurations;
  symbols: string[];
  id_to_value?: IdToValue;
  channel_configuration_id?: number | null;
  sub_group_settings?: {
    max_amount_ratio_limit?: number;
  };
}

export type BotsBulkGeneralData = Record<number, BaseBotGeneralData> & {
  id_to_value?: IdToValue;
};

export enum BotStatus {
  INACTIVE = 1,
  ACTIVE = 2,
  INTERRUPTED = 3,
  STOPPED_BY_USER = 4,
  STOPPED_BY_SYSTEM = 5,
  STOPPED_BY_SETTINGS = 6,
  DELETED = 7,
  STOPPED_BY_VALIDATION = 8,
  SUBSCRIPTION_ENDED = 9,
  SUBSCRIPTION_TYPE_CHANGED = 10,
  STOPPED_BY_UNKNOWN_ERROR = 11,
  EDITED = 12,
  QUOTA_EXCEEDED = 13,
  CLIENT_DELETED = 14,
  CLIENT_DEACTIVATED = 15,
  GROUP_SUBSCRIPTION_ENDED = 16,
  GROUP_DELETED = 17,
  ARCHIVED = 18,
}

export interface ActiveTradeNextOrder {
  price: number;
  distance: number;
}

export interface ActiveTradeLastOrder {
  activity_time: number;
  target_number: number;
  number_of_targets: number;
  time_passed: string;
}

export interface BotActiveTrade extends TradeNextOrders {
  id: number;
  last_entry: ActiveTradeLastOrder;
  last_take_profit: ActiveTradeLastOrder;
  change: {
    USD: number;
    BTC: number;
  };
  entries_filled: {
    amount: {
      USD: number;
      BTC: number;
    };
    number: number;
    total: number;
  };
  entry_price: number;
  main_coin: string;
  invested_quantity: number;
  levels?: TradeLadderLevel[];
  reverse?: boolean;
}

export interface RawTrailingStopData {
  type: string | Off;
  percent: number | null | Off;
  trigger:
    | {
        type: string;
        value: number;
      }
    | Off;
}

export type RawSymbolToMarginTypeAndLeverage = Record<
  string,
  [MarginTypes | undefined, number | undefined, LeverageType | undefined]
>;

export enum OverrideFieldType {
  AMOUNT_PER_TRADE = "override_amount_per_trade",
  CANCEL_ON_PROFIT_BEFORE_ENTRY = "override_cancel_on_profit_before_entry",
  DEFAULT_STOP = "override_default_stop",
  DEFAULT_TAKE_PROFIT = "override_default_take_profit",
  ENTRY_STRATEGY = "override_entry_strategy",
  TAKE_PROFIT_STRATEGY = "override_take_profit_strategy",
  TRAILING_ENTRY = "override_trailing_entry",
  TRAILING_STOP = "override_trailing_stop",
  TRAILING_TAKE_PROFIT = "override_trailing_take_profit",
  LEVERAGE = "override_leverage",
}

export type OverrideData = {
  [key in OverrideFieldType]: boolean;
};

export interface BaseBotData extends AdvancedSettings, OverrideData {
  id: number;
  client_id?: number;
  created_at: number;
  bot_type: BotType;
  options: BotOptions;
  configurations: BotConfigurations;
  exchange: string;
  exchanges?: string[];
  client_name: string;
  symbols: string[] | undefined;
  blacklist_symbols: string[] | undefined;
  number_of_open_trades: number;
  number_of_closed_trades: number;
  default_stop: number | null;
  default_take_profit: number | null;
  leverage: number | null;
  margin_type: MarginTypes | null;
  reverse?: boolean | null;
  buying_strategy: Omit<RawStrategyData, "strategy_style">;
  selling_strategy: Omit<RawStrategyData, "strategy_style"> | null;
  entry_grace_percent: number | null;
  entry_grace_baseline: EntryGraceBaseline | null;
  entry_grace_target_exchange?: string | null;
  number_of_continuous_stops_till_bot_stop: number | null;
  change?: [usdChange: number, btcChange: number];
  active_trade?: BotActiveTrade;
  is_copied: number; // Used as boolean
  is_public: number; // Used as boolean
  trailing_stop: RawTrailingStopData | null;
  trailing_take_profit_last_target_only?: boolean | null;
  take_profit_uuid?: string;
  entry_uuid?: string;
  stop_type?: StopType | null;
  stop_limit_price_reduction?: number | null;
  cancel_on_profit_before_entry?: boolean | null;
  symbol_to_leverage_data?: RawSymbolToMarginTypeAndLeverage;
  channel_configuration_name?: string;
  max_grid_price?: number;
  take_profits_filled?: number;
  number_of_rebalances?: number;
  realised_pnl?: { USD: number; BTC: number };
  days_running: number;
  directions: LongShort[];
  sub_group_id?: number;
  sub_group_name?: string;
  sub_group_image?: string;
  last_trade?: number;
  open_trades_change?: [usdChange: number, btcChange: number];
  is_default_one_click?: boolean;
  skip_without_stoploss: boolean;
  alternative_usd_pairs?: string[];
  follow_futures_as_spot?: boolean | null;
  take_profit_grace_percent?: number | null;
  take_profit_grace_target_exchange?: string | null;
  blacklisted_operation_hours?: BlacklistedOperationHours[];
  sub_group_is_personal?: boolean;
  sub_group_is_active?: boolean;
  keyword_to_symbol_mapping?: Record<string, string>;
  current_exposure?: number;
  last_grid_filled?: ActiveTradeLastOrder;
  long_exposure_ratio?: number;
  lower_price_long_exposure_ratio?: number;
  upper_price_long_exposure_ratio?: number;
}

export interface BotData extends BaseBotData {
  name: string;
  client_id: number;
  status: BotStatus;
  amount_type?: AmountType;
  amount_value?: number;
}

export type BasicBotData = Pick<
  BotData,
  | "symbols"
  | "name"
  | "number_of_closed_trades"
  | "number_of_open_trades"
  | "exchange"
  | "exchanges"
  | "client_id"
  | "client_name"
  | "bot_type"
  | "id"
  | "created_at"
  | "change"
  | "status"
  | "reverse"
  | "margin_type"
  | "leverage"
  | "active_trade"
  | "buying_strategy"
  | "entry_uuid"
  | "take_profit_uuid"
  | "is_copied"
  | "is_public"
  | "realised_pnl"
  | "take_profits_filled"
  | "number_of_rebalances"
  | "sub_group_name"
  | "sub_group_image"
  | "sub_group_id"
  | "last_trade"
  | "directions"
  | "open_trades_change"
  | "is_default_one_click"
  | "amount_type"
  | "amount_value"
  | "sub_group_is_personal"
  | "sub_group_is_active"
  | "max_grid_price"
  | "current_exposure"
  | "last_grid_filled"
  | "long_exposure_ratio"
  | "lower_price_long_exposure_ratio"
  | "upper_price_long_exposure_ratio"
>;

export type StrippedBotData = Pick<BotData, "id" | "name" | "status">;

export type OneClickFollowBotData = Pick<
  BotData,
  | "id"
  | "name"
  | "is_default_one_click"
  | "status"
  | "client_id"
  | "client_name"
  | "options"
  | "sub_group_id"
>;

export interface PublicBotData extends BaseBotData {
  public_data: BotTemplateData;
}

export enum BotTag {
  POPULAR = "popular",
  NEW = "new",
  TRENDING = "trending",
}

export interface BotTemplateData {
  id: number;
  change: number;
  last_90_days_change: number;
  average_monthly_profit: number;
  last_month_change: number;
  graph_data: {
    accumulated_profit: [UnixTimestamp, number][];
    profit_by_day: [UnixTimestamp, number][];
  };
  name: string;
  reverse: boolean;
  symbols: string[];
  exchange: string;
  bot_tags: { [key in BotTag]: boolean } | null;
  uuid: string;
  min_bot_amount: {
    [exchange: string]: { [mainCoin: string]: MainCoinAmountData };
  };
  margin_type: MarginTypes;
  leverage: number;
  sorting_score: number;
  copied_from: boolean;
  days_running: number;
  main_coin: CoinName;
}

export type PublicBotsDataByTag = {
  long_running_score: BotTemplateData[];
  trending_score: BotTemplateData[];
  high_volume_score: BotTemplateData[];
};

export interface BotGraphsData {
  accumulated_profit: UpdatedRawChartData;
  profit_by_day: UpdatedRawChartData;
}

export interface CurrencyBotsGraphsData {
  [botId: number]: { [timestamp: number]: number };
}

export interface BotsGraphsData {
  USD: CurrencyBotsGraphsData;
  BTC: CurrencyBotsGraphsData;
}

export interface BotsGraphsDataResponse {
  profit_by_day: BotsGraphsData;
  accumulated_profit: BotsGraphsData;
  timestamps: number[];
}

export interface GetPublicBotsRequest {
  exchanges?: string[];
  symbols?: string[];
  days_running?: { min: number; max: number };
  long_short?: LongShort;
  valid_bot_amount_only?: boolean;
  by_tag?: boolean;
  strict_symbol?: boolean;
}

export interface BotSymbolPriceData {
  data: number;
  precision: PrecisionConfig;
}

export interface FormattedBotsGraphsData {
  accumulatedProfit: FormattedChartDataPoint[];
  profitByDay: FormattedChartDataPoint[];
}

export type Options = [valuesAndNames: NumberOption[], selectedOption?: number];

export interface NumberOption {
  value: number;
  name: string;
}

export enum TriggerType {
  TARGET = 1,
  PERCENT = 2,
}

export type MarginOptions = [{ [symbol: string]: Options }];

export enum DCABotPageSource {
  CREATE_FORM = 1,
  GENERAL_PAGE = 2,
}

export interface GetBotsDataRequest {
  bot_type: BotType;
  bot_ids?: number[];
  bot_page_source?: DCABotPageSource;
  is_backtesting?: boolean;
  sub_group_id?: number;
  include_archived: boolean;
}

export type GetBotCreateGeneralDataRequest = {
  bot_type: BotType;
  account_id?: number;
  is_channel_configuration?: boolean;
  exchanges?: string[];
  include_leverage?: boolean;
};

export interface GetBotBulkCreateGeneralDataRequest {
  bot_type: BotType;
  account_ids?: number[];
  exchanges?: string[];
}

export interface GetSubGroupBotConfigurationsRequest {
  sub_group_id: number;
  verify_is_admin?: boolean;
}

export interface CalculatePriceDifferenceResponse {
  price_difference: number;
}

export interface CalculatePriceDifferenceRequest {
  number_of_orders: number;
  min_grid_price: number;
  max_grid_price: number;
  price_scale: number;
}

export enum StrategyType {
  PERSONAL = 0,
  EVENLY_DIVIDED = 1,
  ONE_TARGET = 2,
  TWO_TARGETS = 3,
  THREE_TARGETS = 4,
  FIFTY_ON_FIRST_TARGET = 5,
  DECREASING_EXPONENTIAL = 6,
  INCREASING_EXPONENTIAL = 7,
  SKIP_FIRST = 8,
}

export type Timeframe = { start: number; end: number };

export type StartBacktestingRunRequest = CreateBotRequest & {
  is_backtesting: boolean;
  backtesting_time_range: Timeframe;
  backtesting_main_coins_amounts: { [key in string]: number };
};

export type GetBacktestingRunsRequest = {
  account_ids?: number[];
  sub_group_ids?: number[];
  backtesting_run_ids?: number[];
  bot_type?: BotType;
  symbol?: string;
};

export enum BacktestingStatus {
  IN_PROGRESS = 1,
  COMPLETED = 2,
  FAILED = 3,
}

export type BacktestingRunType = {
  id: number;
  bot_id: number;
  name?: string;
  backtesting_time_range: Timeframe;
  status: BacktestingStatus;
  created_at: number;
  job_id: string;
  max_drawdown: number;
  number_of_trades: number;
  realised_pnl: {
    USD: number;
    BTC: number;
  };
  unrealised_pnl: {
    USD: number;
    BTC: number;
  };
  net_pnl?: {
    USD: number;
    BTC: number;
  };
  initial_portfolio_size: {
    USD: number;
    BTC: number;
    main_coin: { [key in string]: number };
  };
  avg_multiplier: number;
  avg_trade_time: number;
  margin_type_to_counter?: {
    [key in MarginTypes]: number;
  };
  asset_to_counter?: {
    [key in string]: number;
  };
  filled_entry_target_to_counter?: {
    [key in number]: number;
  };
  filled_take_profit_target_to_counter?: {
    [key in number]: number;
  };
  pnl_status_to_counter?: {
    [key in string]: number;
  };
  sub_group_id: number;
  sub_group_image?: string;
  sub_group_name?: string;
  client_id?: number;
  client_name: string;
  exchange?: string;
  periodic_stats: {
    headers: string[];
    data: any[];
  };
  symbol?: string;
  original_status?: BacktestingStatus;
};

export type BacktestingGraphData = {
  data: {
    start: [number, [number, number]][];
  };
  filters: null;
  precision: PrecisionConfig;
};

export interface BacktestingJobResults {
  activities_data: {
    data: any[];
    headers: string[];
  };
  accumulated_profit_bot_graph_data: BacktestingGraphData;
  profit_by_day_graph_data: BacktestingGraphData;
  periodic_stats_data: {
    headers: string[];
    data: any[];
  };
  backtesting_run_data: BacktestingRunType;
  current_timestamp: number;
}

export type GetBacktestingRunsResponse = ApiGeneralDataResponse<
  BacktestingRunType[]
>;

export type GetBotsNamesRequest = {
  bot_type: BotType;
  is_backtesting?: boolean;
};

export type GetGroupMainCoinsMultipleExchangesRequest = {
  follow_futures_as_spot?: boolean;
  sub_group_id: number;
  time_range?: Timeframe;
  exchange_to_alternative_usd_pairs: Record<string, string[] | null>;
};

export type GetGroupMainCoinsResponse = {
  [exchange: string]: { [coin: string]: number };
};

export type BacktestingActivity = {
  id: number;
  symbol: string;
  description: string;
  status: string;
  execution_price: number;
  close_reason: string;
  relevant_time: number;
  relevant_amount: number;
  activity_id: number;
  activity_component: string;
  trade_number: number;
  graph_data: Pick<
    TradeGraphData,
    "entry_progress" | "take_profit_progress" | "time_passed"
  > & { profit: number | undefined };
};

export type PeriodicStatsDataPoint = {
  correct_to: number;
  usd_realised_pnl: number;
  btc_realised_pnl: number;
  usd_unrealised_pnl: number;
  btc_unrealised_pnl: number;
};

export enum BacktestingLineChartType {
  REALIZED_PNL = "Realized PnL",
  NET_PNL = "Net PnL",
}

export enum BacktestingSort {
  NAME = "backtesting.sort.backtesting_name",
  GROUP = "backtesting.sort.group_name",
  NET_PNL = "backtesting.sort.net_pnl",
  CREATED = "backtesting.sort.created_date",
  STATUS = "backtesting.sort.status",
  TIME_FRAME = "backtesting.sort.timeframe",
  SYMBOL = "backtesting.sort.symbol",
}

export enum BotConfigurationType {
  SPOT = "spot",
  LEVERAGE = "leverage",
}

export type OperationHoursEvent = {
  start?: Date;
  end?: Date;
  id?: string;
  isActive?: boolean;
};

export type GetBotsDataResponse = ApiGeneralDataResponse<
  BotData[] | BasicBotData[]
>;
